import { CloseOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import {
  type Connection,
  type ConnectionMeter,
  type ConnectionUpdatePayload,
  type MeterUpdatePayload,
  useConnectionMeters,
  useConnections,
  useUpdateConnection,
  useUpdateMeter,
} from "@features/communications/api";
import { connectionMeterColumns } from "@features/communications/config/connectionMeterColumns";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import { resolveUserRole } from "@shared/rbac";
import DrawerForm from "@shared/ui/drawer-form";
import EnterpriseDrawer from "@shared/ui/enterprise-drawer";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStoredUser } from "@shared/useStoredUser";
import {
  Alert,
  App,
  Button,
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
} from "antd";

// Опции интервала опроса (PLAN_OPR2.INTERV - интервал опроса счётчика)
// Значения из справочника cnt.int_opr
const POLL_INTERVAL_OPTIONS = [
  { value: "1m", label: "1 мин" },
  { value: "3m", label: "3 мин" },
  { value: "5m", label: "5 мин" },
  { value: "10m", label: "10 мин" },
  { value: "15m", label: "15 мин" },
  { value: "30m", label: "30 мин" },
  { value: "1h", label: "1 час" },
  { value: "2h", label: "2 часа" },
  { value: "8h", label: "3 раза/сутки" },
  { value: "12h", label: "2 раза/сутки" },
  { value: "1d", label: "1 раз/день" },
  { value: "1n", label: "1 раз/мес" },
];

import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";

interface ExpandedMetersRowProps {
  connectionId: number;
  onEditMeter: (meter: ConnectionMeter) => void;
}

// Компонент для отображения счётчиков соединения
const ExpandedMetersRow = ({ connectionId, onEditMeter }: ExpandedMetersRowProps) => {
  const metersQuery = useConnectionMeters(connectionId);

  if (metersQuery.isLoading) {
    return (
      <div style={{ padding: 16, textAlign: "center" }}>
        <Spin size="small" /> Загрузка счётчиков...
      </div>
    );
  }

  if (!metersQuery.data?.length) {
    return (
      <div style={{ padding: 16, color: "#999" }}>
        Нет счётчиков, привязанных к этому соединению
      </div>
    );
  }

  return (
    <EnterpriseTable<ConnectionMeter>
      columns={connectionMeterColumns}
      dataSource={metersQuery.data}
      rowKey={(row) => String(row.id)}
      pagination={false}
      size="small"
      scroll={{ x: 800 }}
      style={{ margin: "8px 0" }}
      onRow={(record) => ({
        onClick: () => onEditMeter(record),
        style: { cursor: "pointer" },
      })}
    />
  );
};

const ConnectionsPage = () => {
  const { message } = App.useApp();
  const storedUser = useStoredUser();
  const isAdmin = resolveUserRole(storedUser) === "admin";
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects, { required: false });

  const connectionsQuery = useConnections({
    region: selectedObject?.regionId,
    object: selectedObject?.objectId,
    limit: 500,
  });

  const updateMutation = useUpdateConnection();
  const updateMeterMutation = useUpdateMeter();
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [editingMeter, setEditingMeter] = useState<ConnectionMeter | null>(null);
  const [form] = Form.useForm<ConnectionUpdatePayload>();
  const [meterForm] = Form.useForm<MeterUpdatePayload>();
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const connectionMetersQuery = useConnectionMeters(editingConnection?.id);
  const connectionMeters = connectionMetersQuery.data ?? [];

  const handleEdit = (conn: Connection) => {
    setEditingConnection(conn);
    setIsEditing(false);
    setIsDirty(false);
    form.setFieldsValue({
      phone: conn.phone ?? "",
      host: conn.host ?? "",
      tcp_port: conn.tcp_port,
      name: conn.name ?? "",
      interval: parseInterval(conn.interval ?? undefined),
      connect_timeout: conn.connect_timeout,
      retry_count: conn.retry_count,
      state: conn.state,
      time_sync: conn.time_sync,
      all_day: conn.all_day,
      hour_start: conn.hour_start,
      minute_start: conn.minute_start,
      hour_end: conn.hour_end,
      minute_end: conn.minute_end,
      all_week: conn.all_week,
      is_monday: conn.is_monday,
      is_tuesday: conn.is_tuesday,
      is_wednesday: conn.is_wednesday,
      is_thursday: conn.is_thursday,
      is_friday: conn.is_friday,
      is_saturday: conn.is_saturday,
      is_sunday: conn.is_sunday,
    });
  };

  const handleClose = () => {
    setEditingConnection(null);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleEditMeter = (meter: ConnectionMeter) => {
    setEditingMeter(meter);
  };

  // Убираем суффикс "m" из интервала для отображения только числа
  const parseInterval = (interval?: string): string => {
    if (!interval) return "";
    const match = interval.match(/^(\d+)m?$/);
    return match ? match[1] : interval;
  };

  const handleSubmit = async () => {
    if (!editingConnection) return;
    try {
      const values = await form.validateFields();
      await updateMutation.mutateAsync({ id: editingConnection.id, data: values });
      message.success("Подключение обновлено");
      handleClose();
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  const columns: ColumnsType<Connection> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 70,
    },
    {
      title: "Статус",
      dataIndex: "state",
      width: 90,
      render: (value?: number) =>
        value === 0 ? <Tag color="green">Вкл</Tag> : <Tag color="red">Выкл</Tag>,
    },
    {
      title: "Порт",
      dataIndex: "port_name",
      width: 120,
      render: (value?: string, row?: Connection) =>
        value ?? (row?.port_id ? `#${row.port_id}` : "—"),
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      width: 130,
      render: (value?: string) => value || "—",
    },
    {
      title: "Хост:порт",
      dataIndex: "host",
      width: 160,
      render: (_value, row) => {
        if (row.host && row.tcp_port) {
          return `${row.host}:${row.tcp_port}`;
        }
        return row.host ?? "—";
      },
    },
    {
      title: "Название",
      dataIndex: "name",
      width: 180,
      render: (value?: string) => value || "—",
    },
    {
      title: "Переподключ.",
      dataIndex: "interval",
      width: 100,
      render: (value?: string) => value || "—",
    },
    {
      title: "Таймаут",
      dataIndex: "connect_timeout",
      width: 90,
      render: (value?: number) => (value ? `${value} сек` : "—"),
    },
    {
      title: "Попыток",
      dataIndex: "retry_count",
      width: 80,
      render: (value?: number) => value ?? "—",
    },
    {
      title: "Расписание",
      key: "schedule",
      width: 140,
      render: (_value, row) => {
        if (row.all_day === 1) return <Tag>Круглосуточно</Tag>;
        if (row.hour_start !== undefined && row.hour_end !== undefined) {
          const start = `${String(row.hour_start).padStart(2, "0")}:${String(row.minute_start ?? 0).padStart(2, "0")}`;
          const end = `${String(row.hour_end).padStart(2, "0")}:${String(row.minute_end ?? 0).padStart(2, "0")}`;
          return <Tag>{`${start}-${end}`}</Tag>;
        }
        return "—";
      },
    },
    {
      title: "Объект",
      dataIndex: "object_name",
      width: 200,
      render: (_value, row) => {
        if (row.object_name) {
          return (
            <Tag>
              {row.object_name} ({row.region_id}/{row.object_id})
            </Tag>
          );
        }
        if (row.region_id && row.object_id) {
          return <Tag>{`${row.region_id}/${row.object_id}`}</Tag>;
        }
        return "—";
      },
    },
  ];

  const description =
    "Соединения и привязанные счётчики. Раскройте строку (+) для просмотра счётчиков. Клик по строке — карточка подключения.";
  const title = isAdmin ? "Управление подключениями" : "Подключения";

  return (
    <PageLayoutWithHelp title={title}>
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">{description}</Typography.Text>

        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              allowClear
              placeholder="Объект (все доступные)"
              style={{ minWidth: 320 }}
            />
          </FilterRow>
        </FilterBar>

        {!connectionsQuery.isLoading && (connectionsQuery.data?.length ?? 0) === 0 ? (
          <Alert
            type="info"
            showIcon
            message="Подключения не найдены"
            description="Попробуйте изменить фильтры или выберите другой объект."
          />
        ) : (
          <EnterpriseTable<Connection>
            rowKey={(row) => String(row.id)}
            columns={columns}
            dataSource={connectionsQuery.data ?? []}
            loading={connectionsQuery.isLoading}
            scroll={{ x: 1400 }}
            size="small"
            toolbar={{
              reload: true,
              onReload: () => connectionsQuery.refetch(),
            }}
            expandable={{
              expandedRowRender: (record) => (
                <ExpandedMetersRow connectionId={record.id} onEditMeter={handleEditMeter} />
              ),
              rowExpandable: () => true,
            }}
            onRow={(record) => ({
              onClick: () => handleEdit(record),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </Space>

      <EnterpriseDrawer
        title={`Подключение #${editingConnection?.id ?? ""}`}
        open={!!editingConnection}
        onClose={handleClose}
        size="large"
        destroyOnHidden
        actions={
          isAdmin ? (
            <Flex gap={8}>
              <Tooltip title="Закрыть">
                <Button icon={<CloseOutlined />} onClick={handleClose} />
              </Tooltip>
              {!isEditing ? (
                <Tooltip title="Редактировать">
                  <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)} />
                </Tooltip>
              ) : (
                <Tooltip title="Сохранить">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSubmit}
                    loading={updateMutation.isPending}
                    disabled={!isDirty}
                  />
                </Tooltip>
              )}
            </Flex>
          ) : (
            <Tooltip title="Закрыть">
              <Button icon={<CloseOutlined />} onClick={handleClose} />
            </Tooltip>
          )
        }
      >
        {editingConnection && (
          <Tabs
            defaultActiveKey="params"
            items={[
              {
                key: "params",
                label: "Параметры",
                children: (
                  <Form form={form} layout="vertical" onValuesChange={() => setIsDirty(true)}>
                    <Divider>Основные параметры</Divider>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="name" label="Название">
                          <Input placeholder="Название подключения" disabled={!isEditing} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="state"
                          label="Статус"
                          valuePropName="checked"
                          getValueFromEvent={(checked: boolean) => (checked ? 1 : 0)}
                          getValueProps={(value) => ({ checked: value === 1 })}
                        >
                          <Switch
                            checkedChildren="Вкл"
                            unCheckedChildren="Выкл"
                            disabled={!isEditing}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider>Параметры подключения</Divider>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="phone" label="Телефон">
                          <Input placeholder="Номер телефона" disabled={!isEditing} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="host" label="Хост (IP)">
                          <Input placeholder="IP-адрес" disabled={!isEditing} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="tcp_port" label="TCP-порт">
                          <InputNumber
                            min={1}
                            max={65535}
                            style={{ width: "100%" }}
                            disabled={!isEditing}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider>Параметры опроса</Divider>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Form.Item name="interval" label="Интервал (мин)">
                          <Input placeholder="30" disabled={!isEditing} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="connect_timeout" label="Таймаут (сек)">
                          <InputNumber
                            min={1}
                            max={600}
                            style={{ width: "100%" }}
                            disabled={!isEditing}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="retry_count" label="Попыток">
                          <InputNumber
                            min={1}
                            max={10}
                            style={{ width: "100%" }}
                            disabled={!isEditing}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name="time_sync"
                          label="Синхр. времени"
                          valuePropName="checked"
                          getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                          getValueProps={(value) => ({ checked: value === 1 })}
                        >
                          <Checkbox disabled={!isEditing}>Да</Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider>Расписание опроса</Divider>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          name="all_day"
                          label="Круглосуточно"
                          valuePropName="checked"
                          getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                          getValueProps={(value) => ({ checked: value === 1 })}
                        >
                          <Checkbox disabled={!isEditing}>Весь день</Checkbox>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="Начало"
                          shouldUpdate={(prev, curr) =>
                            prev.hour_start !== curr.hour_start ||
                            prev.minute_start !== curr.minute_start
                          }
                        >
                          {() => (
                            <TimePicker
                              format="HH:mm"
                              disabled={!isEditing}
                              value={
                                form.getFieldValue("hour_start") !== undefined
                                  ? dayjs()
                                      .hour(form.getFieldValue("hour_start") ?? 0)
                                      .minute(form.getFieldValue("minute_start") ?? 0)
                                  : undefined
                              }
                              onChange={(time) => {
                                if (time) {
                                  form.setFieldsValue({
                                    hour_start: time.hour(),
                                    minute_start: time.minute(),
                                  });
                                } else {
                                  form.setFieldsValue({
                                    hour_start: undefined,
                                    minute_start: undefined,
                                  });
                                }
                                setIsDirty(true);
                              }}
                              style={{ width: "100%" }}
                            />
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="Конец"
                          shouldUpdate={(prev, curr) =>
                            prev.hour_end !== curr.hour_end || prev.minute_end !== curr.minute_end
                          }
                        >
                          {() => (
                            <TimePicker
                              format="HH:mm"
                              disabled={!isEditing}
                              value={
                                form.getFieldValue("hour_end") !== undefined
                                  ? dayjs()
                                      .hour(form.getFieldValue("hour_end") ?? 0)
                                      .minute(form.getFieldValue("minute_end") ?? 0)
                                  : undefined
                              }
                              onChange={(time) => {
                                if (time) {
                                  form.setFieldsValue({
                                    hour_end: time.hour(),
                                    minute_end: time.minute(),
                                  });
                                } else {
                                  form.setFieldsValue({
                                    hour_end: undefined,
                                    minute_end: undefined,
                                  });
                                }
                                setIsDirty(true);
                              }}
                              style={{ width: "100%" }}
                            />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item label="Дни недели">
                          <Space wrap>
                            <Form.Item
                              name="all_week"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Вся неделя</Checkbox>
                            </Form.Item>
                            <Form.Item
                              name="is_monday"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Пн</Checkbox>
                            </Form.Item>
                            <Form.Item
                              name="is_tuesday"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Вт</Checkbox>
                            </Form.Item>
                            <Form.Item
                              name="is_wednesday"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Ср</Checkbox>
                            </Form.Item>
                            <Form.Item
                              name="is_thursday"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Чт</Checkbox>
                            </Form.Item>
                            <Form.Item
                              name="is_friday"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Пт</Checkbox>
                            </Form.Item>
                            <Form.Item
                              name="is_saturday"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Сб</Checkbox>
                            </Form.Item>
                            <Form.Item
                              name="is_sunday"
                              noStyle
                              valuePropName="checked"
                              getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
                              getValueProps={(value) => ({ checked: value === 1 })}
                            >
                              <Checkbox disabled={!isEditing}>Вс</Checkbox>
                            </Form.Item>
                          </Space>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                ),
              },
              {
                key: "devices",
                label: `Устройства (${connectionMeters.length})`,
                children:
                  connectionMeters.length > 0 ? (
                    <EnterpriseTable<ConnectionMeter>
                      rowKey={(r) => String(r.id)}
                      columns={connectionMeterColumns}
                      dataSource={connectionMeters}
                      loading={connectionMetersQuery.isLoading}
                      size="small"
                      scroll={{ x: 800 }}
                      pagination={false}
                      onRow={(record) => ({
                        onClick: () => handleEditMeter(record),
                        style: { cursor: "pointer" },
                      })}
                    />
                  ) : (
                    <Typography.Text type="secondary">Нет связанных устройств</Typography.Text>
                  ),
              },
            ]}
          />
        )}
      </EnterpriseDrawer>

      {isAdmin && (
        <DrawerForm<MeterUpdatePayload>
          key={editingMeter?.id ?? "new-meter"}
          open={!!editingMeter}
          onClose={() => setEditingMeter(null)}
          onSubmit={async (values) => {
            if (!editingMeter) return;
            try {
              await updateMeterMutation.mutateAsync({ id: editingMeter.id, data: values });
              message.success("Счётчик обновлён");
              setEditingMeter(null);
            } catch {
              message.error("Ошибка при обновлении. Возможно, у вас нет прав администратора.");
            }
          }}
          title={`Редактирование счётчика #${editingMeter?.id ?? ""}`}
          form={meterForm}
          initialValues={
            editingMeter
              ? {
                  name: editingMeter.name ?? "",
                  serial_number: editingMeter.serial_number ?? "",
                  address: editingMeter.address,
                  poll_interval: editingMeter.poll_interval,
                  status: editingMeter.status,
                }
              : undefined
          }
          loading={updateMeterMutation.isPending}
          submitText="Сохранить"
          showReset
          size={720}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="name" label="Название">
                <Input placeholder="Название счётчика" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="serial_number" label="Серийный номер">
                <Input placeholder="Серийный номер" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="address" label="Сетевой адрес">
                <InputNumber min={0} max={255} style={{ width: "100%" }} placeholder="0-255" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="poll_interval" label="Интервал опроса">
                <Select options={POLL_INTERVAL_OPTIONS} placeholder="Выберите интервал" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Статус"
                valuePropName="checked"
                getValueFromEvent={(checked: boolean) => (checked ? 1 : 0)}
                getValueProps={(value) => ({ checked: value === 1 })}
              >
                <Switch checkedChildren="Активен" unCheckedChildren="Неактивен" />
              </Form.Item>
            </Col>
          </Row>
        </DrawerForm>
      )}
    </PageLayoutWithHelp>
  );
};

export default ConnectionsPage;
